import {
    ICell,
    ICellMetric,
    ICellMetricAggregator,
    ICellMetricAggregatorCalcLevel,
    ICellMetricNumberType,
    ICellMetricPushDownMethod,
    ICellMetricPushDownOptions,
    ICellMetricRenderOptions,
    ICellMetricUOM,
    ICellMetricUOMConverters
} from "./PA.HyperRectangle.IF";

import * as _ from "lodash"
import {EvalFunction, parse} from "mathjs"
import {ComputedAggr} from "./ComputedAggr";
import {WB_MetricDefinitionRow} from "../PA.Core/PAdbSchemaRows";
import {IUOMPolicyOptions, UOM} from "../PA.MD.Services/PA.MD.Services.IF";
import {UOMPolicyService} from "../PA.MD.Services/UOMPolicyService";
import * as AsyncLock from "async-lock";


export class CubeSourceMetricDefinition
    implements ICellMetric
{
    private _lock: AsyncLock
    private _metricDefinition: WB_MetricDefinitionRow
    private _metricFormula: string;
    private _metricFunction: EvalFunction
    private _metricAggCalcLevel: ICellMetricAggregatorCalcLevel

    private _options: { [p: string]: any };
    private _renderOptions: ICellMetricRenderOptions;
    private _supportingMetricsIds: string[]
    private _bHidden: boolean
    private _cellMetricUOM: ICellMetricUOM
    private _cellMetricUOMConverters: ICellMetricUOMConverters

    private _leafWriteBackFormula: string;
    private _leafWriteBackFunction: EvalFunction
    private _WriteBackSupportingMetricsIds: string[]
    private _leafWriteBackOvrdMetricId: string
    private _refreshMetricIdsOnRead: string[];
    private _refreshMetricIdsOnWrite: string[];

    constructor(aMtrDfnRow: WB_MetricDefinitionRow)
    {
        const self = this

        //--
        this._metricDefinition = aMtrDfnRow
        this._metricFormula = aMtrDfnRow.cif.FORMULA
        this._leafWriteBackFormula = aMtrDfnRow.cif.WRITEBACK_FORMULA
        this._refreshMetricIdsOnWrite = [aMtrDfnRow.cif.METRIC_ID]
        this._refreshMetricIdsOnRead = []
        this._lock = new AsyncLock()
        switch (aMtrDfnRow.cif.METRIC_TYPE){
            case "csRow":
                this._metricAggCalcLevel = ICellMetricAggregatorCalcLevel.Leaf
                if (aMtrDfnRow.bRowCalcWriteBack && !_.isEmpty(aMtrDfnRow.cif.WRITEBACK_METRIC_ID))
                    this._refreshMetricIdsOnRead.push(aMtrDfnRow.cif.WRITEBACK_METRIC_ID)
                break;

            case "csPerspective":
                this._metricAggCalcLevel = ICellMetricAggregatorCalcLevel.Perspective
                break;

            default:
                this._metricAggCalcLevel = undefined
        }
        this._options =  {}
        this._supportingMetricsIds = []
        this._WriteBackSupportingMetricsIds = []
        this._renderOptions = {
            decimalPlaces: aMtrDfnRow.cif.DECIMAL_COUNT,
            showThousandsSeperator: true,
            numberType: (aMtrDfnRow.bPercent) ? ICellMetricNumberType.Percentage : ICellMetricNumberType.Decimal
        }
        this._bHidden = !aMtrDfnRow.bVisible

        let dctSymbols: {[id:string]: string} = {}
        let parsedFormula = parse(this._metricFormula)
        parsedFormula.traverse((node,path, parent) => {
            if (node.type == "SymbolNode") {
                // @ts-ignore
                dctSymbols[node.name] = node.name
            }
        })
        this._supportingMetricsIds = _.values(dctSymbols)

        this._metricFunction = parsedFormula.compile()

        if(this._metricDefinition.bEditable && (this._metricAggCalcLevel == ICellMetricAggregatorCalcLevel.Perspective)){
            dctSymbols = {}
            this._leafWriteBackOvrdMetricId = aMtrDfnRow.cif.WRITEBACK_METRIC_ID
            this._refreshMetricIdsOnWrite.push(aMtrDfnRow.cif.WRITEBACK_METRIC_ID)

            let writeBackFormula = parse(this._leafWriteBackFormula)
            writeBackFormula.traverse((node,path, parent) => {
                if (node.type == "SymbolNode") {
                    // @ts-ignore
                    dctSymbols[node.name] = node.name
                }
            })
            if('Value' in dctSymbols)
                delete dctSymbols['Value']

            this._WriteBackSupportingMetricsIds = _.values(dctSymbols)
            this._leafWriteBackFunction = writeBackFormula.compile()

            // @ts-ignore
            this.LeafWriteOvrd = (cell: ICell, value: number | string) => {
                let inputs: {[id:string]: any} = {}
                _.forEach(self._WriteBackSupportingMetricsIds, (aMetricId) => {
                    inputs[aMetricId] = cell.getCellMetricValue(aMetricId)
                })
                inputs['Value'] = value
                let results = self._leafWriteBackFunction.evaluate(inputs)
                cell.setCellMetricValue(self._leafWriteBackOvrdMetricId, results)
            }
        }


        this._cellMetricUOM = undefined
        this._cellMetricUOMConverters = undefined

        // Determine the UOM when simple case, empty
        if(_.isEmpty(this._metricDefinition.cif.UOM_NUM) && _.isEmpty(this._metricDefinition.cif.UOM_DENOM)){
            this._cellMetricUOM = {
                NumeratorUOM:  {bUOM: false, UOMCode: "", bCurrency: false, CurrencyCode: ""},
                DenominatorUOM: {bUOM: false, UOMCode: "", bCurrency: false, CurrencyCode: ""},
            }
        }

        // Determine the UOM Converters
        this._cellMetricUOMConverters = {
            NumeratorUOMConverters: _.filter(this._metricDefinition.cif.UOM_CONVERTER_NUM.split(","), (a) => !_.isEmpty(a)),
            DenominatorUOMConverter: _.filter(this._metricDefinition.cif.UOM_CONVERTER_DENOM.split(","), (a) => !_.isEmpty(a))
        }

    }

    get AggregatorCalcLevel(): ICellMetricAggregatorCalcLevel {
        return this._metricAggCalcLevel
    }
    Aggregator(): ICellMetricAggregator {
        return new ComputedAggr(this._metricFormula, this._metricFunction, this._supportingMetricsIds, 0, this._refreshMetricIdsOnRead)
    }

    get Sort(): number { return this._metricDefinition.cif.SORT_ORDER_UI }
    get MetricId(): string { return this._metricDefinition.cif.METRIC_ID }
    get MetricName(): string { return this._metricDefinition.cif.METRIC_NAME }
    get Options(): { [p: string]: any } { return this._options }
    get GroupName(): string { return this._metricDefinition.cif.METRIC_GROUP }
    get RenderOptions(): ICellMetricRenderOptions { return this._renderOptions }
    get SupportingMetricsIds(): string[] { return this._supportingMetricsIds }
    get bIsHidden(): boolean { return this._bHidden }

    get PushDownMethod(): ICellMetricPushDownMethod {
        if(this._metricDefinition.bEditable)
            return ICellMetricPushDownMethod.CopyDown

        return undefined
    }
    get PushDownOptions(): ICellMetricPushDownOptions { return {} }

    get RefreshMetricIdsOnWrite(): string[] { return this._refreshMetricIdsOnWrite }

    get RefreshMetricIdsOnRead(): string[] { return this._refreshMetricIdsOnRead }

    get bIsLeafLevelOnly(): boolean { return false }
    get bIsReadOnly(): boolean { return !this._metricDefinition.bEditable }

    getMetricUOM(options?: IUOMPolicyOptions): ICellMetricUOM | Promise<ICellMetricUOM> {
        const self = this
        if(!_.isEmpty(this._cellMetricUOM))
            return this._cellMetricUOM

        return self._lock.acquire("UOM", async (done) => {
            let cellUOM: ICellMetricUOM = {
                NumeratorUOM: {bUOM: false, UOMCode: "", bCurrency: false, CurrencyCode: ""},
                DenominatorUOM: {bUOM: false, UOMCode: "", bCurrency: false, CurrencyCode: ""},
            }
            try {
                let mdf = self._metricDefinition.cif

                async function setUOM(sUOM: string, sAmtType: string, targetUOM: UOM) {
                    if (_.isEmpty(sUOM)) return
                    let uomParts = sUOM.split(":")
                    let uomType = uomParts[0]
                    let uomValue = uomParts[1]

                    switch (uomType) {
                        case "UOM":
                            targetUOM.bUOM = true
                            targetUOM.UOMCode = uomValue
                            break;

                        case "Currency":
                            targetUOM.bCurrency = true
                            targetUOM.CurrencyCode = uomValue
                            break;

                        case "UOMPolicy":
                            let policy = UOMPolicyService.getUOMPolicy(uomValue)
                            if (!_.isEmpty(policy)) {
                                let uom = await policy.getUOM(options)
                                if (uom)
                                    _.forEach(uom, (value, key) => {
                                        (<any>targetUOM)[key] = value
                                    })
                            }
                            break;
                        default:
                            console.log(`Unknown UOM (${sUOM}) for Metric ${self.MetricId}`)
                            break;
                    }
                }

                if (!_.isEmpty(mdf.UOM_NUM))
                    await setUOM(mdf.UOM_NUM, mdf.UOM_AMOUNT_TYPE_NUM, cellUOM.NumeratorUOM)

                if (!_.isEmpty(mdf.UOM_DENOM))
                    await setUOM(mdf.UOM_DENOM, mdf.UOM_AMOUNT_TYPE_DENOM, cellUOM.DenominatorUOM)
                self._cellMetricUOM = cellUOM
                done(undefined, cellUOM)
            } catch(err) {
                done(err)
            }
        })

    }

    getMetricUOMConverters(): ICellMetricUOMConverters  { return this._cellMetricUOMConverters }

}

